import { useCallback, useEffect } from "react"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { stringRequired } from "@/models/common"
import { useHealthInformationCopilotContext } from "@/modules/patients/contexts/health-information-copilot-context"
import { CommentFormItem } from "@/modules/patients/detail/health-information/comment-form-item"
import { ConfirmedBy } from "@/modules/patients/detail/health-information/confirmed-by"
import { IcdCodeSearch } from "@/modules/patients/detail/health-information/icd-code-search"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { useConfirmDialogStoreContext } from "@/modules/patients/detail/store/useConfirmDialogStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientAllergiesOutputFragment$key } from "@/modules/patients/services/__generated__/PatientAllergiesOutputFragment.graphql"
import { HealthInformationStatus } from "@/modules/patients/services/__generated__/PatientConditionInformationOutputFragment.graphql"
import { Ch_updateAllPatientAllergiesInput } from "@/modules/patients/services/ch/__generated__/chUpdateAllPatientAllergiesMutation.graphql"
import { Ch_verifyHealthInformationInput } from "@/modules/patients/services/ch/__generated__/chVerifyHealthInformationMutation.graphql"
import { De_updateAllPatientAllergiesInput } from "@/modules/patients/services/de/__generated__/deUpdateAllPatientAllergiesMutation.graphql"
import { De_verifyHealthInformationInput } from "@/modules/patients/services/de/__generated__/deVerifyHealthInformationMutation.graphql"
import { PatientAllergiesOutputFragment } from "@/modules/patients/services/PatientAllergiesOutputFragment"
import { usePatientAllergies } from "@/modules/patients/services/usePatientAllergies"
import { useUpdateAllPatientAllergiesMutation } from "@/modules/patients/services/useUpdateAllPatientAllergiesMutation"
import { useVerifyHealthInformation } from "@/modules/patients/services/useVerifyHealthInformation"
import { useDrawerStore } from "@/stores/useDrawerStore"

const allergySchema = z.object({
  id: z.string().optional().nullable(),
  icdCode: stringRequired,
  description: z.string().nullable(),
  comment: z.string().nullable(),
})

const patientAllergiesSchema = z.object({
  allergies: z.array(allergySchema),
  allergyStatus: z.custom<HealthInformationStatus>().optional(),
})

type PatientAllergiesForm = z.infer<typeof patientAllergiesSchema>

const AllergiesDrawer = ({
  canVerify,
  verifiedBy,
  verifiedAt,
}: {
  canVerify: boolean
  verifiedBy: string
  verifiedAt: string
}) => {
  const { t } = useTranslation()
  const isEditMode = useEditMode()
  const patientId = usePatientParam()
  const appointmentId = useAppointmentParam()
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const setDeleteConfirmationDialogParams = useConfirmDialogStoreContext(
    (state) => state.setDeleteConfirmationDialogParams,
  )
  const closeDeleteConfirmationDialog = useConfirmDialogStoreContext((state) => state.closeDeleteConfirmationDialog)
  const country = useCountryParam()
  const handleUpdateAllAllergies = useUpdateAllPatientAllergiesMutation(country)
  const handleVerifyHealthInformation = useVerifyHealthInformation(country)
  const dataRef = usePatientAllergies(patientId, country)
  const data = useFragment<PatientAllergiesOutputFragment$key>(PatientAllergiesOutputFragment, dataRef)

  const selectedAllergy = useHealthInformationCopilotContext((state) => state.allergies.selected)
  const setSelectedAllergy = useHealthInformationCopilotContext((state) => state.allergies.setSelected)
  const resetSelectedAllergy = useHealthInformationCopilotContext((v) => v.allergies.resetSelected)

  const form = useForm<PatientAllergiesForm>({
    resolver: zodResolver(patientAllergiesSchema),
    defaultValues: {
      allergies: data.allergies.map((allergy) => ({
        icdCode: allergy.icdDiagnosis.diagnosisCode,
        description: allergy.icdDiagnosis.text,
        comment: allergy.comment,
        id: allergy.id,
      })),
      allergyStatus: data.status,
    },
  })
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { isDirty },
  } = form
  const { fields, insert, remove } = useFieldArray({ control, name: "allergies" })

  const handleSelectIcdCode = useCallback(
    (diagnosisCode: string, text: string, comment?: string | null) => {
      const allergies = getValues("allergies")
      const sortedCodes = [...allergies.map((field) => field.icdCode), diagnosisCode].sort()
      // Find the last occurrence of this diagnosis code to insert after existing ones
      const addIndex = sortedCodes.lastIndexOf(diagnosisCode)
      insert(
        addIndex,
        { icdCode: diagnosisCode, description: text, comment: comment ?? null },
        { focusName: `allergies.${addIndex}.comment` },
      )
    },
    [insert, getValues],
  )
  const handleVerifyAllergies = () => {
    const verification: De_verifyHealthInformationInput & Ch_verifyHealthInformationInput = {
      patientId,
      healthInformationType: "ALLERGY",
      appointmentId: appointmentId!,
    }
    handleVerifyHealthInformation(verification, closeDrawer)
  }

  const handleSaveAllergies = (data: PatientAllergiesForm) => {
    if (isDirty) {
      const submitData: De_updateAllPatientAllergiesInput & Ch_updateAllPatientAllergiesInput = {
        patientId,
        allergies: data.allergies.map((allergy) => ({
          diagnosisCode: allergy.icdCode,
          comment: allergy.comment,
          id: allergy.id,
        })),
        allergyStatus: data.allergyStatus,
      }

      handleUpdateAllAllergies(submitData, closeDrawer)
    } else closeDrawer()
  }

  const handleDeleteAllergy = (index: number, description: string | null) => {
    setDeleteConfirmationDialogParams({
      title: t("patient.healthInformation.deleteHealthInformation.title", {
        item: description ?? "",
      }),
      content: t("episodeOfCare.deleteProgressNote.content"),
      handleDelete: () => {
        const allergies = getValues("allergies")
        if (allergies.length === 1) {
          setValue("allergyStatus", "UNKNOWN")
        }
        remove(index)
        closeDeleteConfirmationDialog()
      },
    })
  }

  const RADIO_ITEMS: { value: HealthInformationStatus; label: string }[] = [
    {
      value: "UNKNOWN",
      label: t("patient.healthInformation.emptyQuestions.Unknown"),
    },
    {
      value: "NONE",
      label: t("common.no"),
    },
  ]

  const handleClose = useCallback(() => {
    FaroInsightsWrapper.pushEvent("healthInformation_allergy_close")
    closeDrawer()
    resetSelectedAllergy()
  }, [closeDrawer, resetSelectedAllergy])

  useEffect(() => {
    if (selectedAllergy) {
      const allergies = getValues("allergies")
      const isExistingAllergies = allergies.find(
        (field) =>
          field.icdCode === selectedAllergy.diagnosis.diagnosisCode && field.comment === selectedAllergy.comment,
      )
      if (!isExistingAllergies) {
        handleSelectIcdCode(
          selectedAllergy.diagnosis.diagnosisCode,
          selectedAllergy.diagnosis.text,
          selectedAllergy.comment,
        )
      }
      setSelectedAllergy(null)
    }
  }, [selectedAllergy, insert, setSelectedAllergy, getValues, handleSelectIcdCode])

  useDirtyDrawerMaker(isDirty, handleClose)

  return (
    <Drawer open onOpenChange={handleClose}>
      <DrawerContent data-testid="health-allergy-drawer-content">
        <DrawerClose className="top-2 right-2" />
        <DrawerHeader className="border-b py-2">
          <DrawerTitle
            className="animate-fade-in-bottom text-on-surface-subdued text-sm leading-10 font-medium"
            data-testid="allergies-drawer-title">
            {t("patient.healthInformation.allergies.title")}
          </DrawerTitle>
          {isEditMode && (
            <IcdCodeSearch
              searchType="ALLERGY"
              searchName="allergy"
              onIcdCodeSelected={handleSelectIcdCode}
              searchInputPlaceHolder={t("patient.healthInformation.allergies.addAllergy")}
              addedIcdCodes={[]}
            />
          )}
        </DrawerHeader>
        <DrawerBody className="p-0" data-testid="allergy-drawer-body-section">
          <ScrollWrapper className="p-4">
            <div className="flex flex-col gap-4">
              <FormProvider {...form}>
                {fields.length === 0 && isEditMode && (
                  <div className="flex flex-col gap-2">
                    <span className="text-on-surface-subdued text-sm leading-5" data-testid="allergy-status-question">
                      {t("patient.healthInformation.allergies.emptyQuestions")}
                    </span>
                    <FormField
                      name="allergyStatus"
                      control={control}
                      render={({ field: { value: val, onChange } }) => (
                        <RadioGroup
                          data-testid="allergy-unknown-select"
                          defaultValue={data.status}
                          className="auto-cols-min grid-flow-col"
                          value={val}
                          onValueChange={onChange}>
                          {RADIO_ITEMS.map(({ value, label }) => (
                            <RadioGroupItem
                              key={label}
                              data-testid={`allergy-radio-${value}`}
                              value={value.toString()}
                              id={`option-${value}`}>
                              <RadioGroupLabel htmlFor={`option-${value}`}>{label}</RadioGroupLabel>
                            </RadioGroupItem>
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </div>
                )}
                {fields.length !== 0 &&
                  fields.map((field, index) => {
                    return (
                      <CommentFormItem
                        key={field.id}
                        dataTestId="allergy"
                        isEditMode={isEditMode}
                        title={`${field.icdCode} - ${field.description}`}
                        onDelete={() => handleDeleteAllergy(index, field.description)}
                        commentFieldName={`allergies.${index}.comment`}
                      />
                    )
                  })}
              </FormProvider>
            </div>
          </ScrollWrapper>
        </DrawerBody>
        <DrawerFooter className="justify-end gap-2 overflow-hidden" data-testid="allery-drawer-footer-section">
          {verifiedBy !== "" && <ConfirmedBy verifiedBy={verifiedBy} verifiedAt={verifiedAt} />}
          <Button variant="secondary" onClick={handleClose} data-testid="allergy-close-btn">
            {isEditMode ? t("common.button.cancel") : t("common.button.close")}
          </Button>
          {isEditMode && !canVerify && (
            <Button
              onClick={() => {
                FaroInsightsWrapper.pushEvent("healthInformation_allergy_save")
                handleSubmit(handleSaveAllergies)()
              }}
              disabled={!isDirty}
              data-testid="allergy-save-btn">
              {t("common.button.save")}
            </Button>
          )}
          {isEditMode && canVerify && (
            <Button
              onClick={() => {
                handleSubmit(handleSaveAllergies)()
                handleVerifyAllergies()
              }}
              data-testid="allergy-verify-btn">
              {t("common.button.verify")}
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export { AllergiesDrawer, type PatientAllergiesForm }
